import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ToastMsg from "../../components/ToastMsg";
import PortalPopup from "../../components/PortalPopup";
import LogoutConfirmation from "../../components/LogoutConfirmation";
import ExportModalTables from "../../components/ExportModalTables";
import NavigationPanel from "../../components/NavigationPanel";
import CTAs from "../../components/CTAs";
import Dropdown from "../../components/Dropdown";
import Footer from "../../components/Footer";
import SettingNav from "../../components/settingNav";
import { Edit2, Trash } from "iconsax-reactjs";
import InviteUser from "../../components/InviteUser";

const SettingsSecurityPage = () => {
  const navigate = useNavigate();
  const [isToastMsgOpen, setToastMsgOpen] = useState(false);
  const [isLogoutConfirmationOpen, setLogoutConfirmationOpen] = useState(false);
  const [isLogoutConfirmation1Open, setLogoutConfirmation1Open] = useState(false);
  const [isExportModalTablesOpen, setExportModalTablesOpen] = useState(false);
  const [isInviteUserOpen, setInviteUserOpen] = useState(false);

  const [teamMembers, setTeamMembers] = useState([]);
  const [companyData, setCompanyData] = useState(null);
  const [isLoadingTeam, setIsLoadingTeam] = useState(true);

  const API_URL = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("authToken");

  useEffect(() => {
    fetchCompanyData();
  }, []);

  const fetchCompanyData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/subscription/company`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        setCompanyData(data.company);
        setTeamMembers(data.users || []);
      }
    } catch (error) {
      console.error("Error fetching company data:", error);
    } finally {
      setIsLoadingTeam(false);
    }
  };

  const getUserId = (user) => user.id || user._id;

  const handleResendInvitation = async (userId) => {
    try {
      const response = await fetch(
        `${API_URL}/api/subscription/users/${userId}/resend-invitation`,
        { method: "POST", headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Failed to resend invitation");
      alert("Invitation resent successfully!");
    } catch (error) {
      console.error("Error resending invitation:", error);
      alert(`Error: ${error.message}`);
    }
  };

  const handleCancelInvitation = async (userId) => {
    if (!window.confirm("Are you sure you want to cancel this invitation?")) return;
    try {
      // Optimistically remove from UI
      setTeamMembers((prev) => prev.filter((m) => getUserId(m) !== userId));

      const response = await fetch(
        `${API_URL}/api/subscription/users/${userId}/cancel-invitation`,
        { method: "DELETE", headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await response.json();
      if (!response.ok) {
        // Rollback on failure
        fetchCompanyData();
        throw new Error(data.message || "Failed to cancel invitation");
      }
      // Sync with server to ensure consistency
      fetchCompanyData();
    } catch (error) {
      console.error("Error canceling invitation:", error);
      alert(`Error: ${error.message}`);
    }
  };

  const handleRemoveUser = async (userId) => {
    if (!window.confirm("Are you sure you want to remove this user?")) return;
    try {
      // Optimistically remove from UI immediately
      setTeamMembers((prev) => prev.filter((m) => getUserId(m) !== userId));

      const response = await fetch(
        `${API_URL}/api/subscription/users/${userId}`,
        { method: "DELETE", headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await response.json();
      if (!response.ok) {
        // Rollback on failure
        fetchCompanyData();
        throw new Error(data.message || "Failed to remove user");
      }
      // Sync with server to ensure consistency
      fetchCompanyData();
    } catch (error) {
      console.error("Error removing user:", error);
      alert(`Error: ${error.message}`);
    }
  };

  const openInviteUser = useCallback(() => setInviteUserOpen(true), []);
  const closeInviteUser = useCallback(() => setInviteUserOpen(false), []);
  const openToastMsg = useCallback(() => setToastMsgOpen(true), []);
  const closeToastMsg = useCallback(() => setToastMsgOpen(false), []);
  const closeLogoutConfirmation = useCallback(() => setLogoutConfirmationOpen(false), []);
  const closeLogoutConfirmation1 = useCallback(() => setLogoutConfirmation1Open(false), []);
  const openExportModalTables = useCallback(() => setExportModalTablesOpen(true), []);
  const closeExportModalTables = useCallback(() => setExportModalTablesOpen(false), []);

  const dropdownContents = ["5 minutes", "10 minutes", "15 minutes"];

  const [privacySettings, setPrivacySettings] = useState({ enableAutoLogout: false });
  const handlePrivacySettingChange = (setting) => {
    setPrivacySettings((prev) => ({ ...prev, [setting]: !prev[setting] }));
  };

  const admins = teamMembers.filter((member) => member.isCompanyOwner);
  const users = teamMembers.filter((member) => !member.isCompanyOwner);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);

  const toggleSelect = (id) => {
    setSelectedUsers((prev) =>
      prev.includes(id) ? prev.filter((uid) => uid !== id) : [...prev, id]
    );
  };
  const selectAll = () => {
    if (selectedUsers.length === admins.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(admins.map((u) => getUserId(u)));
    }
  };
  const toggleSelect2 = (id) => {
    setSelectedMembers((prev) =>
      prev.includes(id) ? prev.filter((uid) => uid !== id) : [...prev, id]
    );
  };
  const selectAll2 = () => {
    if (selectedMembers.length === users.length) {
      setSelectedMembers([]);
    } else {
      setSelectedMembers(users.map((u) => getUserId(u)));
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;1,9..40,400&display=swap');

        html, body { background: rgb(241, 245, 249) !important; }

        .dd-root * { box-sizing: border-box; }
        .dd-root {
          font-family: 'DM Sans', sans-serif;
          background: rgb(241, 245, 249) !important;
          min-height: 100vh;
        }
        .dd-root.bg-white,
        .dd-root.bg-neutrals-neutral-50,
        .dd-root.bg-gray-50 {
          background: rgb(241, 245, 249) !important;
        }

        .dd-card {
          background: #ffffff;
          border: 1px solid #E2E8F0;
          border-radius: 16px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.06), 0 2px 12px rgba(0,0,0,0.04);
          transition: box-shadow 0.2s ease;
          position: relative;
          overflow: hidden;
        }

        .dd-section-divider {
          border: none;
          border-top: 1px solid #E2E8F0;
          margin: 0;
        }

        .dd-section-label {
          font-size: 14px;
          font-weight: 700;
          color: #0F172A;
          margin: 0 0 12px 0;
        }

        .dd-section-sublabel {
          font-size: 13px;
          color: #64748B;
          margin: 2px 0 0 0;
        }

        .dd-checkbox-row {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 14px;
          color: #1E293B;
        }

        .dd-checkbox {
          width: 18px;
          height: 18px;
          border-radius: 5px;
          border: 1.5px solid #CBD5E1;
          accent-color: #2563EB;
          cursor: pointer;
          flex-shrink: 0;
        }
        .dd-checkbox:disabled { opacity: 0.4; cursor: not-allowed; }

        .dd-info-row {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          color: #94A3B8;
        }

        .dd-btn-secondary {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 8px 18px;
          background: #ffffff; border: 1px solid #E2E8F0; border-radius: 8px;
          font-family: 'DM Sans', sans-serif;
          font-size: 13px; font-weight: 600; color: #0F172A;
          cursor: pointer; box-shadow: 0 1px 2px rgba(0,0,0,0.05);
          transition: border-color 0.15s, background 0.15s;
        }
        .dd-btn-secondary:hover { border-color: #94A3B8; background: #F8FAFC; }

        .dd-btn-primary {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 8px 18px;
          background: #2563EB; border: 1px solid #2563EB; border-radius: 8px;
          font-family: 'DM Sans', sans-serif;
          font-size: 13px; font-weight: 600; color: #ffffff;
          cursor: pointer;
          transition: background 0.15s, box-shadow 0.15s;
        }
        .dd-btn-primary:hover { background: #1D4ED8; box-shadow: 0 4px 12px rgba(37,99,235,0.25); }
        .dd-btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }

        .dd-edit-link {
          font-size: 13px; font-weight: 600; color: #2563EB;
          cursor: pointer; text-decoration: none;
        }
        .dd-edit-link:hover { text-decoration: underline; }

        .dd-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 14px;
        }
        .dd-table th {
          text-align: left;
          padding: 10px 16px;
          background: #F8FAFC;
          color: #64748B;
          font-weight: 600;
          font-size: 13px;
          border-bottom: 1px solid #E2E8F0;
        }
        .dd-table td {
          padding: 12px 16px;
          border-bottom: 1px solid #F1F5F9;
          color: #1E293B;
        }
        .dd-table tr:last-child td {
          border-bottom: none;
        }
        .dd-actions {
          display: flex;
          gap: 12px;
          justify-content: flex-end;
        }
        .dd-actions button {
          background: none;
          border: none;
          cursor: pointer;
          color: #64748B;
          padding: 4px;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .dd-actions button:hover {
          background: #F1F5F9;
          color: #0F172A;
        }
        .dd-action-link {
          font-size: 13px;
          font-weight: 600;
          color: #2563EB;
          background: none;
          border: none;
          cursor: pointer;
        }
        .dd-action-link.danger { color: #EF4444; }
        .dd-action-link:hover { text-decoration: underline; }
        .dd-badge {
          display: inline-block;
          padding: 2px 10px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 500;
          background: #F1F5F9;
          color: #475569;
        }
      `}</style>

      <div
        className="dd-root w-full overflow-visible"
        style={{ background: "rgb(241, 245, 249)", minHeight: "100vh" }}
      >
        <NavigationPanel />

        <div
          style={{
            position: "absolute",
            top: 136,
            width: "100%",
            padding: "0 96px 64px",
            display: "flex",
            flexDirection: "column",
            gap: 24,
          }}
        >
          {/* Page heading */}
          <div style={{ borderBottom: "1px solid #E2E8F0", paddingBottom: 24 }}>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "#0F172A" }}>
              Settings
            </h1>
            <p style={{ margin: "4px 0 0", fontSize: 14, color: "#64748B" }}>
              Get your billing information, generate/review invoices.
            </p>
          </div>

          {/* Body: sidenav + content */}
          <div style={{ display: "flex", alignItems: "flex-start", gap: 32 }}>
            <SettingNav />

            <div
              className="dd-card"
              style={{
                flex: 1,
                padding: 28,
                display: "flex",
                flexDirection: "column",
                gap: 24,
              }}
            >
              {/* Card header */}
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  paddingBottom: 20,
                  borderBottom: "1px solid #E2E8F0",
                }}
              >
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: "#0F172A" }}>
                    Security
                  </div>
                  <div style={{ fontSize: 13, color: "#64748B", marginTop: 2 }}>
                    Manage access, authentication, and data protection.
                  </div>
                </div>
                <div style={{ display: "flex", gap: 10, flexShrink: 0 }}>
                  <button className="dd-btn-secondary">Cancel</button>
                  <button className="dd-btn-primary" onClick={openToastMsg}>
                    Save
                  </button>
                </div>
              </div>

              {/* Team management section */}
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <div>
                    <p className="dd-section-label" style={{ margin: 0 }}>
                      Team management
                    </p>
                    <p className="dd-section-sublabel">
                      Manage your team members and their account permissions here.
                    </p>
                  </div>
                  <button className="dd-btn-secondary" onClick={openInviteUser}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path d="M6 12h12M12 18V6" stroke="#2d404e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    Add team member
                  </button>
                </div>

                {/* Admin users */}
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <p className="dd-section-label" style={{ margin: 0 }}>Admin users</p>
                  <p className="dd-section-sublabel">
                    Admin can add and remove users and manage organisation level settings.
                  </p>
                  <div style={{ marginTop: 8 }}>
                    <table className="dd-table">
                      <thead>
                        <tr>
                          <th style={{ width: "50%" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <input
                                type="checkbox"
                                className="dd-checkbox"
                                checked={selectedUsers.length === admins.length && admins.length > 0}
                                onChange={selectAll}
                              />
                              Name
                            </div>
                          </th>
                          <th>Last login</th>
                          <th>Status</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {isLoadingTeam ? (
                          <tr>
                            <td colSpan="4" style={{ textAlign: "center", padding: 24, color: "#64748B" }}>
                              Loading...
                            </td>
                          </tr>
                        ) : admins.length === 0 ? (
                          <tr>
                            <td colSpan="4" style={{ textAlign: "center", padding: 24, color: "#64748B" }}>
                              No admin users found
                            </td>
                          </tr>
                        ) : (
                          admins.map((user) => (
                            <tr key={getUserId(user)}>
                              <td>
                                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                  <input
                                    type="checkbox"
                                    className="dd-checkbox"
                                    checked={selectedUsers.includes(getUserId(user))}
                                    onChange={() => toggleSelect(getUserId(user))}
                                  />
                                  <div>
                                    <div style={{ fontWeight: 600, color: "#0F172A" }}>{user.name}</div>
                                    <div style={{ fontSize: 12, color: "#94A3B8" }}>{user.email}</div>
                                  </div>
                                </div>
                              </td>
                              <td>
                                {user.lastLogin
                                  ? new Date(user.lastLogin).toLocaleDateString("en-US", {
                                      year: "numeric", month: "short", day: "numeric",
                                    })
                                  : "Never"}
                              </td>
                              <td>
                                <span className="dd-badge">
                                  {user.invitationStatus === "pending" ? "Invitation pending" : user.licenseStatus || "Active"}
                                </span>
                              </td>
                              <td>
                                <div className="dd-actions">
                                  {user.invitationStatus === "pending" ? (
                                    <>
                                      <button className="dd-action-link" onClick={() => handleResendInvitation(getUserId(user))}>
                                        Resend
                                      </button>
                                      <button className="dd-action-link danger" onClick={() => handleCancelInvitation(getUserId(user))}>
                                        Cancel
                                      </button>
                                    </>
                                  ) : (
                                    <>
                                      <button onClick={() => handleRemoveUser(getUserId(user))}>
                                        <Trash size={18} />
                                      </button>
                                      <button>
                                        <Edit2 size={18} />
                                      </button>
                                    </>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                <hr className="dd-section-divider" />

                {/* Account users */}
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <p className="dd-section-label" style={{ margin: 0 }}>Account users</p>
                  <p className="dd-section-sublabel">
                    Account users can access and review the changes.
                  </p>
                  <div style={{ marginTop: 8 }}>
                    <table className="dd-table">
                      <thead>
                        <tr>
                          <th style={{ width: "50%" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <input
                                type="checkbox"
                                className="dd-checkbox"
                                checked={selectedMembers.length === users.length && users.length > 0}
                                onChange={selectAll2}
                              />
                              Name
                            </div>
                          </th>
                          <th>Last login</th>
                          <th>Status</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {isLoadingTeam ? (
                          <tr>
                            <td colSpan="4" style={{ textAlign: "center", padding: 24, color: "#64748B" }}>
                              Loading...
                            </td>
                          </tr>
                        ) : users.length === 0 ? (
                          <tr>
                            <td colSpan="4" style={{ textAlign: "center", padding: 24, color: "#64748B" }}>
                              No account users found
                            </td>
                          </tr>
                        ) : (
                          users.map((user) => (
                            <tr key={getUserId(user)}>
                              <td>
                                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                  <input
                                    type="checkbox"
                                    className="dd-checkbox"
                                    checked={selectedMembers.includes(getUserId(user))}
                                    onChange={() => toggleSelect2(getUserId(user))}
                                  />
                                  <div>
                                    <div style={{ fontWeight: 600, color: "#0F172A" }}>{user.name}</div>
                                    <div style={{ fontSize: 12, color: "#94A3B8" }}>{user.email}</div>
                                  </div>
                                </div>
                              </td>
                              <td>
                                {user.lastLogin
                                  ? new Date(user.lastLogin).toLocaleDateString("en-US", {
                                      year: "numeric", month: "short", day: "numeric",
                                    })
                                  : "Never"}
                              </td>
                              <td>
                                <span className="dd-badge">
                                  {user.invitationStatus === "pending" ? "Invitation pending" : user.licenseStatus || "Active"}
                                </span>
                              </td>
                              <td>
                                <div className="dd-actions">
                                  {user.invitationStatus === "pending" ? (
                                    <>
                                      <button className="dd-action-link" onClick={() => handleResendInvitation(getUserId(user))}>
                                        Resend
                                      </button>
                                      <button className="dd-action-link danger" onClick={() => handleCancelInvitation(getUserId(user))}>
                                        Cancel
                                      </button>
                                    </>
                                  ) : (
                                    <>
                                      <button onClick={() => handleRemoveUser(getUserId(user))}>
                                        <Trash size={18} />
                                      </button>
                                      <button>
                                        <Edit2 size={18} />
                                      </button>
                                    </>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <hr className="dd-section-divider" />

              {/* Logout options */}
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <p className="dd-section-label" style={{ margin: 0 }}>Logout options</p>
                <p className="dd-section-sublabel">Set your logout preferences.</p>
                <label className="dd-checkbox-row">
                  <input
                    type="checkbox"
                    className="dd-checkbox"
                    checked={privacySettings.enableAutoLogout}
                    onChange={() => handlePrivacySettingChange("enableAutoLogout")}
                  />
                  Enable Automatic Logout After Inactivity
                </label>
                {privacySettings.enableAutoLogout && (
                  <div style={{ marginTop: 8, maxWidth: 320 }}>
                    <Dropdown
                      title="Select timeout duration"
                      placeholder="Select duration"
                      dropdownContents={dropdownContents}
                      disabled={!privacySettings.enableAutoLogout}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          <Footer />
        </div>
      </div>

      {/* Modals */}
      {isToastMsgOpen && (
        <PortalPopup overlayColor="rgba(113, 113, 113, 0.3)" placement="Centered" onOutsideClick={closeToastMsg}>
          <ToastMsg onClose={closeToastMsg} />
        </PortalPopup>
      )}
      {isLogoutConfirmationOpen && (
        <PortalPopup overlayColor="rgba(113, 113, 113, 0.3)" placement="Centered" onOutsideClick={closeLogoutConfirmation}>
          <LogoutConfirmation onClose={closeLogoutConfirmation} />
        </PortalPopup>
      )}
      {isLogoutConfirmation1Open && (
        <PortalPopup overlayColor="rgba(113, 113, 113, 0.3)" placement="Centered" onOutsideClick={closeLogoutConfirmation1}>
          <LogoutConfirmation onClose={closeLogoutConfirmation1} />
        </PortalPopup>
      )}
      {isInviteUserOpen && (
        <PortalPopup overlayColor="rgba(113, 113, 113, 0.3)" placement="Centered" onOutsideClick={closeInviteUser}>
          <InviteUser onClose={closeInviteUser} onInviteSuccess={fetchCompanyData} />
        </PortalPopup>
      )}
      {isExportModalTablesOpen && (
        <PortalPopup overlayColor="rgba(113, 113, 113, 0.3)" placement="Centered" onOutsideClick={closeExportModalTables}>
          <ExportModalTables onClose={closeExportModalTables} />
        </PortalPopup>
      )}
    </>
  );
};

export default SettingsSecurityPage;